"use client";

import {
	Select as AriakitSelect,
	SelectItem as AriakitSelectItem,
	SelectPopover as AriakitSelectPopover,
	SelectProvider,
} from "@ariakit/react";
import { Check, Close, DiscoverTune, KeyboardArrowDown } from "@ellaandil/ui";
import { useIntersectionObserver } from "@trydig/ui/hooks";
import { startTransition } from "react";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Typography } from "@/components/ui/Typography";
import { FilterModalTrigger } from "@/features/products/ProductListingPage/components/FilterModal";
import { FloatingFilterBar } from "@/features/products/ProductListingPage/components/FloatingFilterBar";
import { usePLP } from "@/features/products/ProductListingPage/hooks/PLPContext";
import { useProductListStateValue } from "@/features/products/ProductListingPage/hooks/useProductListState";
import { useTranslations } from "@/i18n/client";
import { useFooterProximity } from "@/lib/hooks/useFooterProximity";
import { useModal } from "@/lib/hooks/useModal";

export const Filterbar = () => {
	const t = useTranslations();
	const { loaded, total } = useProductListStateValue();
	const { data, sortId, setSort } = usePLP();
	const { isOpen: filtersOpen } = useModal("filters");

	const sorts = data?.pages[0]?.sorts ?? [];
	// When no `?sort=` is set, Depict returns the first entry's ranking by
	// default — so use sorts[0] as the effective active label.
	const activeSort = sorts.find((s) => s.id === sortId) ?? sorts[0];
	const placeholder = activeSort?.label ?? t("Sort");

	// Show the floating bar only after the inline Filterbar scrolls UP out of
	// view — i.e. the user has actually passed it. PLP headers can be tall
	// (hero + category cards), so the inline bar is often below the fold at
	// page load; `!isIntersecting` alone would flash the floating bar in that
	// case. `boundingClientRect.top < 0` separates "scrolled past" (above
	// viewport) from "not reached yet" (below viewport).
	//
	// Also hide while the filter modal is open or the footer is near, so the
	// bar doesn't sit redundantly behind the modal or overlap the footer.
	const [filterbarRef, entry] = useIntersectionObserver<HTMLDivElement>({
		threshold: 0,
	});
	const hasScrolledPast =
		entry !== null && !entry.isIntersecting && entry.boundingClientRect.top < 0;
	const isFooterNear = useFooterProximity();
	const floatingVisible = hasScrolledPast && !isFooterNear && !filtersOpen;

	return (
		<>
			<div ref={filterbarRef} className="flex justify-between items-center">
				<div className="flex justify-between w-full lg:w-auto lg:justify-baseline flex-row-reverse lg:flex-row items-center gap-grid-grid-gutter-lg">
					<FilterModalTrigger asChild>
						<Button
							variant="outline"
							color="secondary"
							size="large"
							className="flex items-center gap-0.5"
						>
							{filtersOpen ? (
								<>
									<Close width={18} height={18} />
									<span>{t("Close")}</span>
								</>
							) : (
								<>
									<DiscoverTune width={18} height={18} />
									<span>{t("Filter")}</span>
								</>
							)}
						</Button>
					</FilterModalTrigger>
					<Typography variant="body-sm-reg">
						{t("Showing {{loaded}} out of {{total}} products", { loaded, total })}
					</Typography>
				</div>
				{sorts.length > 0 && (
					<div className="hidden lg:block">
						<SelectProvider
							value={sortId ?? ""}
							setValue={(v) => {
								const id = typeof v === "string" && v ? v : undefined;
								// First sort entry is Depict's default ranking — re-selecting it
								// should clear the URL param rather than write the explicit id.
								const isDefault = id === sorts[0]?.id;
								startTransition(() => setSort(id && !isDefault ? id : undefined));
							}}
						>
							<AriakitSelect
								render={
									<Button
										variant="outline"
										color="secondary"
										size="large"
										className="flex items-center gap-0.5"
									/>
								}
							>
								<span>{placeholder}</span>
								<KeyboardArrowDown width={18} height={18} />
							</AriakitSelect>
							<AriakitSelectPopover
								gutter={8}
								className="bg-background-bg-neutral-1 border border-border-medium shadow-lg z-50 max-h-60 overflow-auto py-grid-component-spacing-2xs min-w-(--popover-anchor-width) outline-none"
							>
								{sorts.map((sort) => {
									const isActive = sort.id === (activeSort?.id ?? "");
									return (
										<AriakitSelectItem
											key={sort.id}
											value={sort.id}
											className="flex w-full items-center gap-grid-component-spacing-2xs px-grid-component-spacing-md py-grid-component-spacing-2xs cursor-pointer text-sm text-text-dark-text-1 hover:bg-fade-1 data-active-item:bg-fade-1 outline-none"
										>
											<span className="flex-1">{sort.label}</span>
											<Check
												width={16}
												height={16}
												aria-hidden
												className={isActive ? "opacity-100" : "opacity-0"}
											/>
										</AriakitSelectItem>
									);
								})}
							</AriakitSelectPopover>
						</SelectProvider>
					</div>
				)}
			</div>
			<FloatingFilterBar visible={floatingVisible} />
		</>
	);
};

export const FilterbarSkeleton = () => {
	const t = useTranslations();
	return (
		<div className="flex justify-between items-center">
			<div className="flex items-center gap-grid-grid-gutter-lg">
				<Skeleton fitContent>
					<Button
						variant="outline"
						color="secondary"
						size="large"
						className="flex items-center gap-0.5"
					>
						<DiscoverTune width={18} height={18} />

						<span>{t("Filter")}</span>
					</Button>
				</Skeleton>
				<Skeleton fitContent>
					<Typography variant="body-sm-reg">
						{t("Showing {{loaded}} out of {{total}} products", { loaded: 0, total: 0 })}
					</Typography>
				</Skeleton>
			</div>
			<Skeleton fitContent>
				<Button
					variant="outline"
					color="secondary"
					size="large"
					className="flex items-center gap-0.5"
				>
					<span>{t("Sort")}</span>
					<KeyboardArrowDown width={18} height={18} />
				</Button>
			</Skeleton>
		</div>
	);
};
